<script>
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js";
import {useUserStore} from "@/stores/Setting/User.js";
import {onMounted, ref} from "vue";
import WorkerImage from "@/views/Worker/Components/WorkerImage.vue";
import axios from "axios";

export default {
    components: {WorkerImage},
    setup() {
        const {loading, total,} = useSharedData();
        const {$i18n, $router} = useTools();
        
        const userStore = useUserStore();
        const securityStore = useSecurityStore();
        
        const actions = [
           {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => securityStore.authorities['UPDATE_PRIVILEGE_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'users.edit', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => securityStore.authorities['UPDATE_PRIVILEGE_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'users.delete', params: {id: item.id}})
                },
            },
        ];
        const headers = [
            {text: "...", align: "left", sortable: false, value: "action"},
            {
                text: "",
                align: "start",
                sortable: false,
                value: "photo",
                fixed: true,
            },
            {
                text: $i18n.t("common.username"),
                align: "left",
                sortable: true,
                value: "username"
            },
            {
                text: $i18n.t("common.lastname"),
                align: "left",
                sortable: true,
                value: "lastName"
            },
            {
                text: $i18n.t("common.firstname"),
                align: "left",
                sortable: true,
                value: "firstName"
            },
            {
                text: $i18n.t("module.worker"),
                align: "left",
                sortable: true,
                value: "workerName"
            },
            {
                text: $i18n.t("common.email"),
                align: "left",
                sortable: true,
                value: "email"
            },
            {
                text: $i18n.t("module.role"),
                align: "left",
                sortable: false,
                value: "roleName"
            }
        ];
        const key = ref(null);
        
        function findAll() {
            loading.value = true;
            axios.get('/users').then((response) => {
                total.value = response.data.length;
                userStore.setUsers(response.data);
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function view(item) {
            $router.push({name: "users.view", params: {id: item.id},});
        }
        
        onMounted(() => findAll());
        
        return {
            securityStore,
            userStore,
            loading,
            total,
            headers,
            actions,
            key,
            view,
            findAll
        }
    },
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.users')"
            :total="total"
        >
            <template #rightToolbar>
                <w-btn-reload :loading="loading" @press="findAll"/>
                <w-btn-add
                    :show="securityStore.authorities['UPDATE_PRIVILEGE_PARAM']"
                    @press="$router.push({ name: 'users.add' })"
                />
            </template>
            
            <template #content>
                <v-container fluid>
                    <v-row>
                        <v-col cols="12" md="4">
                            <w-text-field
                                v-model="key"
                                :label="`${$t('common.search')}`"
                                append-icon="mdi-account-search"
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <w-data-table
                    :headers="headers"
                    :actions="actions"
                    :items="userStore.users"
                    :search="key"
                    sort-by="name"
                    :loading="loading"
                    @rowDbClick="view"
                >
                    <template v-slot:[`item.action`]="{ item }">
                        <v-menu bottom right>
                            <template v-slot:activator="{ on }">
                                <v-btn dark icon color="primary" v-on="on">
                                    <v-icon>mdi-dots-horizontal</v-icon>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <v-list-item
                                    v-if="securityStore.authorities['UPDATE_PRIVILEGE_PARAM']"
                                    @click="$router.push({name: 'users.edit', params: { id: item.id }})"
                                >
                                    <v-list-item-icon>
                                        <v-icon v-text="'mdi-pencil-box-multiple'" color="orange"></v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>{{ $t("common.edit") }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    v-if="securityStore.authorities['UPDATE_PRIVILEGE_PARAM']"
                                    @click="$router.push({name: 'users.change_password', params: { id: item.id }})"
                                >
                                    <v-list-item-icon>
                                        <v-icon v-text="'mdi-lock-reset'" color="purple"></v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>{{ $t('user.reset_password') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    v-if="securityStore.authorities['UPDATE_PRIVILEGE_PARAM']"
                                    @click="$router.push({name: 'users.delete', params: { id: item.id }})"
                                >
                                    <v-list-item-icon>
                                        <v-icon v-text="'mdi-trash-can'" color="error"></v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>
                                        {{ $t("common.delete") }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                    
                    <template v-slot:[`item.photo`]="{ item }">
                        <WorkerImage
                            :worker-id="item.workerId"
                            disable-card
                            readonly
                        />
                    </template>
                    <template v-slot:[`item.username`]="{ item }">
                        <w-chip :text="item.username"/>
                    </template>
                    <template v-slot:[`item.lastName`]="{ item }">
                        <w-inline-text bold :text="item.lastName"/>
                    </template>
                    <template v-slot:[`item.firstName`]="{ item }">
                         <w-inline-text bold :text="item.firstName"/>
                    </template>
                    <template v-slot:[`item.workerName`]="{ item }">
                         <w-inline-text :text="item.workerName"/>
                    </template>
                    <template v-slot:[`item.email`]="{ item }">
                         <w-inline-text :text="item.email"/>
                    </template>
                    <template v-slot:[`item.roleName`]="{ item }">
                        <w-chip color="teal" :text="item.roleName"/>
                    </template>
                </w-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>